import type { Socket } from "socket.io-client";
import { getSocketInstance } from "./socket";

export type TtsAudioChunkEvent = {
  message_id: string;
  tab_id?: string | null;
  index: number;
  audio_url: string;
  is_last: boolean;
};

export type TtsAudioErrorEvent = {
  message_id: string;
  tab_id?: string | null;
  index?: number;
  error?: string;
};

export type TtsSessionOwner = symbol;

type ChunkListener = (payload: TtsAudioChunkEvent) => void;
type ErrorListener = (payload: TtsAudioErrorEvent) => void;

let chunkListener: ChunkListener | null = null;
let errorListener: ErrorListener | null = null;
let activeOwner: TtsSessionOwner | null = null;
let boundSocket: Socket | null = null;
let connectHooked = false;

function onChunk(payload: TtsAudioChunkEvent) {
  chunkListener?.(payload);
}

function onError(payload: TtsAudioErrorEvent) {
  errorListener?.(payload);
}

function isSocketReady(socket: Socket | null): boolean {
  return !!(socket?.connected && socket.id);
}

function hookSocketLifecycle(socket: Socket) {
  if (connectHooked) return;
  connectHooked = true;
  // After reconnect, re-bind in case listeners were cleared or the socket was replaced.
  socket.io.on("reconnect", () => {
    boundSocket = null;
    ensureTtsAudioSocketAttached();
  });
}

/** Keep socket listeners so the first chunk after 202 is never missed. */
export function ensureTtsAudioSocketAttached(): boolean {
  const socket = getSocketInstance();
  if (!socket) return false;

  hookSocketLifecycle(socket);

  if (boundSocket === socket) return true;

  if (boundSocket) {
    boundSocket.off("tts_audio_chunk", onChunk);
    boundSocket.off("tts_audio_error", onError);
  }

  socket.on("tts_audio_chunk", onChunk);
  socket.on("tts_audio_error", onError);
  boundSocket = socket;
  return true;
}

/**
 * If the socket is already up, return immediately.
 * Only wait when it is still connecting (typical right after page load).
 */
export async function waitForTtsSocketReady(
  timeoutMs = 5_000,
): Promise<Socket | null> {
  const existing = getSocketInstance();
  if (isSocketReady(existing)) {
    ensureTtsAudioSocketAttached();
    return existing;
  }

  if (!existing) return null;

  if (!existing.connected) {
    try {
      existing.connect();
    } catch {
      // ignore — connect event / poll will settle
    }
  }

  return new Promise((resolve) => {
    let settled = false;
    let socket: Socket | null = existing;

    const finish = (result: Socket | null) => {
      if (settled) return;
      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      socket?.off("connect", onConnect);
      if (result) ensureTtsAudioSocketAttached();
      resolve(result);
    };

    const check = () => {
      socket = getSocketInstance();
      if (isSocketReady(socket)) finish(socket);
    };

    const onConnect = () => check();
    const poll = window.setInterval(check, 50);
    const timeout = window.setTimeout(() => finish(null), timeoutMs);
    socket.on("connect", onConnect);
    check();
  });
}

export function claimTtsAudioSession(
  owner: TtsSessionOwner,
  onChunkHandler: ChunkListener,
  onErrorHandler: ErrorListener,
): void {
  ensureTtsAudioSocketAttached();
  activeOwner = owner;
  chunkListener = onChunkHandler;
  errorListener = onErrorHandler;
  // Let other speaker buttons clear their local loading/playing UI.
  window.dispatchEvent(
    new CustomEvent("tts-session-replaced", { detail: { owner } }),
  );
}

// Only the owner of the active session may clear global listeners.
export function releaseTtsAudioSession(owner: TtsSessionOwner): void {
  if (activeOwner !== owner) return;
  activeOwner = null;
  chunkListener = null;
  errorListener = null;
}

export function isTtsAudioSessionOwner(owner: TtsSessionOwner): boolean {
  return activeOwner === owner;
}
